import { Col, Row, Table } from 'antd';
import { clsx } from 'clsx';
import { Key } from 'antd/lib/table/interface';
import type { ColumnsType } from 'antd/es/table';
import { PropsWithChildren, useEffect, useState } from 'react';

import { Dealer, User } from '@types';
import { Container } from '@components';
import cls from './SharedFarmers.module.css';
import { managerHttp } from '@network';
import { AddFarmer } from '@pages/dealer/DealerAccount/AddFarmer';
import { useRedirect } from 'react-admin';


enum StatusBadges {
  info = 'info',
  info_light = 'info_light',
  success = 'success',
  success_light = 'success_light',
  error = 'error',
  error_light = 'error_light',
  error_dark = 'error_dark',
  active = 'active',
  inactive = 'inactive',
  not_available = 'not_available',
}

interface Props {
  dealer: Dealer;
}

interface StatusProps {
  className?: string;
  status?: StatusBadges;
}

type UserStatusType = 'active' | 'inactive' | 'not_available';
const userStatuses: Record<UserStatusType, string> = {
  active: 'Activo',
  inactive: 'Inactivo',
  not_available: 'Pendiente',
};

const Status = ({ status = StatusBadges.info, children }: PropsWithChildren<StatusProps>) => (
  <span className={clsx(cls.status, cls[status])}>{children || '???'}</span>
);

const columns: ColumnsType<User> = [
  {
    title: 'Full Name',
    dataIndex: 'fullName',
    width: 400,
    sorter: (a: User, b: User) => `${a.fullName}`.localeCompare(`${b.fullName}`),
  },
  {
    title: 'Phone',
    dataIndex: 'phone',
    render: formatPhone,
    sorter: (a: User, b: User) => `${a.phone}`.localeCompare(`${b.phone}`),
    width: 260,
    align: 'center',
  },
  {
    title: 'Type',
    dataIndex: 'isCompany',
    render: (isCompany: boolean) => (isCompany ? 'Company' : 'Individual'),
    sorter: (a: User, b: User) => `${a.isCompany}`.localeCompare(`${b.isCompany}`),
    width: 100,
    align: 'center',
  },
  {
    title: 'Status',
    dataIndex: 'status',
    width: 155,
    align: 'center',
    sorter: (a: User, b: User) => `${a.status}`.localeCompare(`${b.status}`),
    render: (status: StatusBadges) => (
      <Status className={cls.status}
        status={StatusBadges[status ? status as UserStatusType : 'inactive']}>
        {userStatuses[status ? status as UserStatusType : 'inactive']}
      </Status>
    ),
  },
  {
    title: 'Registration Date',
    dataIndex: 'registeredAt',
    sorter: (a: User, b: User) =>
      new Date(a.registeredAt).getTime() -
      new Date(b.registeredAt).getTime(),
    render: dateFormat,
    width: 263,
    align: 'center',
  },
];

export const SharedFarmers = ({ dealer }: Props) => {
  const redirect = useRedirect();
  const [isLoading, setIsLoading] = useState(false);
  const [farmersList, setFarmersList] = useState<User[]>([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });

  const getFarmersList = async () => {
    if (dealer.manager) {
      setIsLoading(true);
      const [data] = await managerHttp.getFarmers(dealer.manager?.id);
      data && setFarmersList(data);
      setIsLoading(false);
    }
  };

  const onRowClick = (row: User) => ({
    onClick: () => redirect(`/user/${row.id}?initial`),
  });

  useEffect(() => {
    getFarmersList();
  }, []);

  return (
    <Container max className={cls.container}>
      <Row gutter={0} className={cls.wrapper} align="stretch">
        <Col xs={24} className={cls.wrapperCol}>
          <div className={cls.tableWrapper}>
            {dealer.manager ? <AddFarmer manager={dealer.manager} /> : null}
            <Table
              className={cls.table}
              rowClassName={cls.tableRow}
              columns={columns}
              dataSource={farmersList || []}
              rowKey="id"
              onRow={onRowClick}
              loading={isLoading}
              pagination={{
                className: cls.tablePagination,
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: farmersList.length,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100'],
                locale: {
                  items_per_page: '',
                },
                position: ['bottomLeft'],
                size: 'small',
                showTotal: (total, range) =>
                  `${range[0]} - ${range[1]} of ${total}`,
              }}
              onChange={(paginationConfig) => {
                setPagination({
                  current: paginationConfig.current || 1,
                  pageSize: paginationConfig.pageSize || 10,
                });
              }}
            />
          </div>
        </Col>
      </Row>
    </Container>
  );
};

function dateFormat(dt: string): string {
  const date = new Date(dt);

  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const year = date.getUTCFullYear();

  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');

  return `${day}/${month}/${year}, ${hours}:${minutes}`;
}

function formatPhone(phone: string): string {
  const hasPlus = phone.startsWith('+');
  const digits = phone.replace(/\D/g, ''); // убираем всё кроме цифр

  const groups: string[] = [];
  let rest = digits;

  // 4 цифры с конца
  if (rest.length > 4) {
    groups.unshift(rest.slice(-4));
    rest = rest.slice(0, -4);
  }

  // ещё 4
  if (rest.length > 4) {
    groups.unshift(rest.slice(-4));
    rest = rest.slice(0, -4);
  }

  // ещё 2
  if (rest.length > 2) {
    groups.unshift(rest.slice(-2));
    rest = rest.slice(0, -2);
  }

  if (rest.length) {
    groups.unshift(rest);
  }

  return `${hasPlus ? '+' : ''}${groups.join(' ')}`;
}
